import { getAccessToken, handleUnauthorized } from './api/axios';
import { getEnv } from './env';

/** A failed HTTP response from `postSse`, carrying the status code. */
export class HttpError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
    this.name = 'HttpError';
  }
}

/** POST that streams `data: <json>` lines (NestJS @Sse), with one token-refresh retry. */
export const postSse = async <T>(
  path: string,
  body: unknown,
  onEvent: (event: T) => void,
  signal?: AbortSignal,
  retried = false,
): Promise<void> => {
  const response = await fetch(`${getEnv('VITE_API_URL')}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getAccessToken()}` },
    body: JSON.stringify(body),
    signal,
  });

  if (response.status === 401 && !retried) {
    if (await handleUnauthorized()) return postSse(path, body, onEvent, signal, true);
    throw new Error('Session expired. Please log in again.');
  }
  if (!response.ok) {
    const errorData = (await response.json().catch(() => ({}))) as { message?: string };
    throw new HttpError(errorData.message || `HTTP error ${response.status}`, response.status);
  }

  const reader = response.body?.getReader();
  if (!reader) throw new Error('No response body');
  const decoder = new TextDecoder();
  let buffer = '';
  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';
      for (const line of lines) {
        if (!line.startsWith('data: ')) continue;
        const data = line.slice(6).trim();
        if (!data) continue;
        try {
          onEvent(JSON.parse(data) as T);
        } catch {
          // skip malformed SSE lines
        }
      }
    }
  } finally {
    reader.releaseLock();
  }
};
